<template>
    <div class="mx-auto">
        <!-- Page Header -->
        <div class="page-header">
            <AppHeader>
                <template #underTitle>
                    <small
                        v-can-access.permissions="$Permissions.READ_SERVICES"
                        v-if="!loaderStore.isLoading('services.fetch') && servicesStore.current"
                        class="inline-flex items-center justify-center rounded-full bg-green-300 text-gray-800 text-xs font-medium px-2 py-0.5 shadow-sm">
                        <span><b>Service:</b> {{ servicesStore.current?.name }}</span>
                    </small>
                    <div v-can-access.permissions="$Permissions.READ_SERVICES" v-else class="snippet ml-3" data-title=".dot-flashing">
                        <div class="stage">
                            <div class="dot-flashing" />
                        </div>
                    </div>
                </template>
            </AppHeader>
        </div>

        <!-- Toolbar: Search and Create -->
        <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2 w-1/3">
                <SearchInput
                    :custom-class="'w-96'"
                    :loading="loaderStore.isLoading('kafkaUsers.fetch')"
                    placeholder="Search by username or IP"
                    @search="(input) => (searchQuery = input)" />
            </div>
            <button
                v-can-access.permissions="[$Permissions.WRITE_KAFKA_ACCESS]"
                aria-label="Create Kafka User"
                class="btn btn-primary rounded-lg flex items-center gap-1"
                :disabled="loaderStore.isLoading('kafkaUsers.fetch')"
                @click="openAddModal">
                <span
                    aria-hidden="true"
                    class="inline-flex items-center rounded-lg border border-transparent bg-cyan-950 p-0.5 text-white shadow-xs hover:bg-cyan-900 mr-1">
                    <PlusIcon class="h-5 w-5" />
                </span>
                <span class="font-medium text-sm">Create New Kafka User</span>
            </button>
        </div>

        <!-- Data Table -->
        <AppLoader
            :force-loading="loaderStore.isLoading('kafkaUsers.fetch')"
            default-height="20vh"
            default-width="'100%'"
            class="shadow-md rounded-md">
            <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-300 border" aria-label="Kafka Users table">
                    <thead class="bg-gray-50">
                        <tr>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Username</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Whitelisted IPs</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Proxy Endpoint</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">ACLs</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"></th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                        <template v-if="!loaderStore.isLoading('kafkaUsers.fetch')">
                            <template v-if="filteredUsers.length">
                                <template v-for="user in filteredUsers" :key="user.username">
                                    <tr class="hover:bg-gray-50 transition">
                                        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            <div class="flex items-center">
                                                <UserIcon class="h-5 w-5 text-gray-500 mr-2" />
                                                <span>{{ user.username }}</span>
                                            </div>
                                        </td>
                                        <td class="px-6 py-4">
                                            <div class="flex flex-wrap gap-1">
                                                <template
                                                    v-for="(ip, idx) in user.whitelist_ips
                                                        ? user.whitelist_ips
                                                              .split(',')
                                                              .map((v) => v.trim())
                                                              .filter((v) => v)
                                                        : []"
                                                    :key="idx">
                                                    <span class="bg-gray-100 text-gray-800 text-xs rounded px-2 py-1">
                                                        {{ ip }}
                                                    </span>
                                                </template>
                                            </div>
                                        </td>
                                        <td class="px-6 py-4">
                                            <div class="flex flex-col gap-1">
                                                <template
                                                    v-for="(ep, idx) in user.kafka_proxy_endpoint
                                                        ? user.kafka_proxy_endpoint
                                                              .split(',')
                                                              .map((v) => v.trim())
                                                              .filter((v) => v)
                                                        : []"
                                                    :key="idx">
                                                    <span class="text-xs text-gray-500">{{ ep }}</span>
                                                </template>
                                            </div>
                                        </td>
                                        <td class="px-6 py-4">
                                            <div class="flex flex-wrap gap-1">
                                                <template v-for="(acl, idx) in user.kafka_acls.slice(0, 3)" :key="idx">
                                                    <span class="bg-gray-100 text-gray-800 text-xs rounded px-2 py-1">
                                                        {{ acl.name }} ({{ acl.operation.toUpperCase() }},
                                                        {{ acl.resource_pattern_type.toUpperCase() }} / {{ acl.resource.toUpperCase() }})
                                                    </span>
                                                </template>
                                                <button
                                                    v-if="user.kafka_acls.length > 3"
                                                    @click="toggleExpand(user.username)"
                                                    type="button"
                                                    class="text-sm text-cyan-900 hover:underline ml-1">
                                                    {{ expandedUser === user.username ? 'Hide' : 'View All' }}
                                                </button>
                                            </div>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                v-can-access.permissions="[$Permissions.WRITE_KAFKA_ACCESS]"
                                                aria-label="Edit"
                                                @click="openEditModal(user)"
                                                class="text-gray-600 hover:text-cyan-900 mr-2">
                                                <PencilSquareIcon class="h-5 w-5" />
                                            </button>
                                            <button
                                                v-can-access.permissions="[$Permissions.WRITE_KAFKA_ACCESS]"
                                                aria-label="Delete"
                                                @click="handleDeleteUser(user)"
                                                class="text-gray-600 hover:text-red-600">
                                                <TrashIcon class="h-5 w-5" />
                                            </button>
                                        </td>
                                    </tr>
                                    <tr v-if="expandedUser === user.username">
                                        <td colspan="5" class="bg-gray-50 px-6 py-4 text-sm text-gray-700">
                                            <ul class="list-disc list-inside">
                                                <li v-for="(acl, idx) in user.kafka_acls" :key="idx">
                                                    {{ acl.name }} - {{ acl.operation.toUpperCase() }} ({{ acl.resource_pattern_type.toUpperCase() }},
                                                    {{ acl.resource.toUpperCase() }})
                                                </li>
                                            </ul>
                                        </td>
                                    </tr>
                                </template>
                            </template>
                            <tr v-else>
                                <td colspan="99" class="text-center p-2">
                                    <div class="p-5 flex flex-col w-full max-w-sm px-4 mx-auto">
                                        <div class="p-3 mx-auto text-cyan-950 bg-green-100 rounded-full">
                                            <MagnifyingGlassIcon class="h-7 w-7" />
                                        </div>
                                        <p class="mt-2 text-cyan-950 text-xs">No matching records found</p>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </AppLoader>

        <!-- Add/Edit Modal -->
        <KafkaUserAddEditModal :is-open="isModalOpen" :user="selectedUser" @update:isOpen="(val) => (isModalOpen = val)" @save="handleSaveUser" />
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import AppHeader from '@/components/AppHeader.vue';
import SearchInput from '@/components/global/SearchInput.vue';
import AppLoader from '@/components/AppLoader.vue';
import { PlusIcon, PencilSquareIcon, TrashIcon, UserIcon } from '@heroicons/vue/24/solid';
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline';
import { useKafkaUsersStore } from '@/stores/kafkaUsers.store';
import { useLoaderStore } from '@/stores/loader.store';
import { useServicesStore } from '@/stores/services.store';
import { Permissions } from '@/utils/constants';
import useConfirmBeforeAction from '@/composables/useConfirmBeforeAction';
import KafkaUserAddEditModal from '@/components/kafkaAccess/KafkaUserAddEditModal.vue';

const kafkaUsersStore = useKafkaUsersStore();
const loaderStore = useLoaderStore();
const servicesStore = useServicesStore();

const searchQuery = ref('');
const isModalOpen = ref(false);
const selectedUser = ref(null);
const expandedUser = ref(null);

const filteredUsers = computed(() => {
    const q = searchQuery.value.toLowerCase();
    return kafkaUsersStore.all.filter((u) => u.username.toLowerCase().includes(q) || u.whitelist_ips.toLowerCase().includes(q));
});

onMounted(() => {
    kafkaUsersStore.fetch();
});

const openAddModal = () => {
    selectedUser.value = null;
    isModalOpen.value = true;
};

const openEditModal = (user) => {
    selectedUser.value = user;
    isModalOpen.value = true;
};

const handleSaveUser = async (payload) => {
    try {
        if (selectedUser.value) {
            await kafkaUsersStore.updateUser(selectedUser.value.username, payload);
        } else {
            await kafkaUsersStore.addUser(payload);
        }
        isModalOpen.value = false;
    } catch (error) {
        console.error('Error saving user:', error);
    }
};

const handleDeleteUser = (user) => {
    useConfirmBeforeAction(
        'delete',
        async () => {
            await kafkaUsersStore.deleteUser(user.username);
        },
        { entity: 'Kafka user', name: user.username },
        () => {}
    );
};

const toggleExpand = (username) => {
    expandedUser.value = expandedUser.value === username ? null : username;
};
</script>

<style scoped>
/* Add component-specific styles if needed */
</style>
